import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Check,
  Close,
  DragIndicator,
  ViewSidebarOutlined,
  WebAssetOutlined,
  SplitscreenOutlined,
  KeyboardOutlined,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import ChatAgentPanel from "./ChatAgentPanel";
import ChatConversationSidebar from "./ChatConversationSidebar";
import { useWorkspaceLayout } from "../context/WorkspaceLayoutContext";
import { useDockFrame } from "./layout/WorkspaceShell";
import { formatShortcut } from "../hooks/useWorkspaceShortcuts";
import { INKY_PROMPT_EVENT } from "./pagekit/askInky";

/** Composer textarea inside ChatAgentPanel (its aria-label is the stable hook). */
const COMPOSER_SELECTOR = 'textarea[aria-label="Message Inky"]';
/** Composer Send button inside ChatAgentPanel (replaced by Stop while a turn runs). */
const SEND_BUTTON_SELECTOR = 'button[aria-label="Send"]';
/** Animation frames to wait for the composer (or its Send button) to appear. */
const PLACE_PROMPT_MAX_FRAMES = 40;
const SUBMIT_PROMPT_MAX_FRAMES = 20;

const DOCK_POSITIONS = [
  { side: "right", label: "Dock right", icon: <ViewSidebarOutlined fontSize="small" /> },
  { side: "left", label: "Dock left", icon: <ViewSidebarOutlined fontSize="small" sx={{ transform: "scaleX(-1)" }} /> },
  { side: "bottom", label: "Dock bottom", icon: <SplitscreenOutlined fontSize="small" /> },
  { side: "floating", label: "Float", icon: <WebAssetOutlined fontSize="small" /> },
];

/**
 * Put text into a React-controlled textarea as if the user typed it (fires onChange), then
 * focus it with the caret at the end. Used to seed Inky's composer without sending.
 * @param {HTMLTextAreaElement} textarea - Composer.
 * @param {string} text - Text to place.
 * @returns {void}
 */
function seedTextarea(textarea, text) {
  const setter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value")?.set;
  if (setter) setter.call(textarea, text);
  else textarea.value = text; // eslint-disable-line no-param-reassign
  textarea.dispatchEvent(new Event("input", { bubbles: true }));
  textarea.focus({ preventScroll: true });
  const end = text.length;
  try {
    textarea.setSelectionRange(end, end);
  } catch {
    /* not focusable yet */
  }
}

/**
 * Inky's dock. Rendered once by the app shell and never unmounted: the shell moves the same
 * element between right/left/bottom, floating and the narrow-screen sheet. The chat panel and
 * History both stay mounted (the inactive one is hidden), so switching views, closing the dock
 * or navigating never drops Inky's thread, draft or a running turn.
 *
 * Seeding: pages call `useWorkspaceLayout().openInky({ prompt })` or `askInky(prompt)` from
 * pagekit (a cancelable INKY_PROMPT_EVENT window event the dock accepts). The dock opens, places
 * the text in the composer and focuses it. It sends only when the caller asked with
 * `send: true` and no turn is running; otherwise the user reviews the prompt and presses Send.
 * @param {object} props - Component props.
 * @param {string} props.contextSummary - On-screen context summary.
 * @param {Array<object>} props.tools - Inky tool registry (src/assistant/agentTools.js).
 * @param {string} [props.accountId] - Active account id for tier-based Gemini on server.
 * @param {object} props.contextData - Entity data and active ids for the current route.
 * @param {string | null} [props.contextHint] - Optional one-line context subtitle.
 * @returns {JSX.Element} Dock content.
 */
function ChatAgentDock({ contextSummary, tools, accountId, contextData, contextHint }) {
  const { pathname } = useLocation();
  const { dock, setDock, openInky, pendingPrompt, consumePendingPrompt, clearComposerChips, composerFocusToken, setShortcutsHelpOpen } = useWorkspaceLayout();
  const frame = useDockFrame();
  const rootRef = useRef(null);
  const submitFrameRef = useRef(0);
  const [view, setView] = useState("chat");
  const [positionMenuAnchor, setPositionMenuAnchor] = useState(null);
  /**
   * Screen + conversation the chat header title belongs to, or null for the neutral label.
   * Owned here so it survives the panel re-rendering in a new frame.
   * @type {[{conversationId: string, pathname: string} | null, Function]}
   */
  const [titleScope, setTitleScope] = useState(null);

  /**
   * Claim the chat header title for the current screen (or clear it when given no conversation).
   * Idempotent so repeat claims for the same thread/screen do not re-render the panel.
   * @param {string | null} conversationId - Conversation whose title may head the panel here.
   * @returns {void}
   */
  const claimTitleScope = useCallback(
    (conversationId) => {
      setTitleScope((previous) => {
        if (!conversationId) return previous === null ? previous : null;
        if (previous?.conversationId === conversationId && previous?.pathname === pathname) {
          return previous;
        }
        return { conversationId, pathname };
      });
    },
    [pathname]
  );

  /** "New chat" from History (the thread is created on the first message). */
  const handleNewChat = useCallback(() => {
    claimTitleScope(null);
    setView("chat");
  }, [claimTitleScope]);

  /**
   * A conversation picked in History: its title heads the chat panel on this screen.
   * @param {string} conversationId - Conversation the user opened.
   * @returns {void}
   */
  const handleSelectConversation = useCallback((conversationId) => {
    claimTitleScope(conversationId);
    setView("chat");
  }, [claimTitleScope]);

  // Memoized so layout changes (dock moves, palette, focus mode) do not re-render the thread.
  const chatPanel = useMemo(
    () => (
      <ChatAgentPanel
        contextSummary={contextSummary}
        tools={tools}
        accountId={accountId}
        contextData={contextData}
        contextHint={contextHint}
        titleScope={titleScope}
        onClaimTitleScope={claimTitleScope}
      />
    ),
    [contextSummary, tools, accountId, contextData, contextHint, titleScope, claimTitleScope]
  );
  const historyPanel = useMemo(
    () => <ChatConversationSidebar onNewChat={handleNewChat} onSelectConversation={handleSelectConversation} />,
    [handleNewChat, handleSelectConversation]
  );

  /**
   * The composer element, when the chat view is mounted.
   * @returns {HTMLTextAreaElement|null} Textarea.
   */
  const getComposer = useCallback(() => rootRef.current?.querySelector(COMPOSER_SELECTOR) || null, []);

  /**
   * Press the composer's Send button once React has committed the seeded text. Gives up (the
   * prompt stays in the composer for the user) when a turn is running, which swaps Send for Stop,
   * or the composer no longer holds the seeded text (the user started typing).
   * @param {string} text - Seeded prompt.
   * @returns {void}
   */
  const submitSeededPrompt = useCallback(
    (text) => {
      cancelAnimationFrame(submitFrameRef.current);
      const expected = text.trim();
      let tries = 0;
      const attempt = () => {
        const composer = getComposer();
        if (!composer || composer.value.trim() !== expected) return;
        const button = rootRef.current?.querySelector(SEND_BUTTON_SELECTOR);
        if (button && !button.disabled) {
          button.click();
          return;
        }
        tries += 1;
        if (tries < SUBMIT_PROMPT_MAX_FRAMES) submitFrameRef.current = requestAnimationFrame(attempt);
      };
      submitFrameRef.current = requestAnimationFrame(attempt);
    },
    [getComposer]
  );

  useEffect(() => () => cancelAnimationFrame(submitFrameRef.current), []);

  // Seed a prompt from a page ("Ask Inky about this selection"); send only when asked to.
  useEffect(() => {
    if (!pendingPrompt) return undefined;
    setView("chat");
    let tries = 0;
    let raf = 0;
    const place = () => {
      const composer = getComposer();
      if (composer && composer.offsetParent !== null) {
        seedTextarea(composer, pendingPrompt.text);
        consumePendingPrompt(pendingPrompt.id);
        if (pendingPrompt.send) submitSeededPrompt(pendingPrompt.text);
        return;
      }
      tries += 1;
      if (tries < PLACE_PROMPT_MAX_FRAMES) raf = requestAnimationFrame(place);
      else {
        // Never placed: drop it, and the guidance chips that belonged to it.
        consumePendingPrompt(pendingPrompt.id);
        clearComposerChips();
      }
    };
    raf = requestAnimationFrame(place);
    return () => cancelAnimationFrame(raf);
  }, [pendingPrompt, consumePendingPrompt, clearComposerChips, getComposer, submitSeededPrompt]);

  // "Ask Inky" from any page (pagekit askInky): accept the prompt so the caller skips its
  // clipboard fallback. The turn's page context comes from the current route, so the event's
  // informational `context` is not needed here.
  useEffect(() => {
    const onInkyPrompt = (event) => {
      const prompt = typeof event.detail?.prompt === "string" ? event.detail.prompt.trim() : "";
      if (!prompt) return;
      event.preventDefault();
      openInky({ prompt, send: event.detail.send === true });
    };
    window.addEventListener(INKY_PROMPT_EVENT, onInkyPrompt);
    return () => window.removeEventListener(INKY_PROMPT_EVENT, onInkyPrompt);
  }, [openInky]);

  // Focus the composer when the dock is opened from a shortcut or "Ask Inky".
  useEffect(() => {
    if (!composerFocusToken) return undefined;
    setView("chat");
    let tries = 0;
    let raf = 0;
    const focus = () => {
      const composer = getComposer();
      if (composer && composer.offsetParent !== null) {
        composer.focus({ preventScroll: true });
        return;
      }
      tries += 1;
      if (tries < 20) raf = requestAnimationFrame(focus);
    };
    raf = requestAnimationFrame(focus);
    return () => cancelAnimationFrame(raf);
  }, [composerFocusToken, getComposer]);

  const isFloating = frame.variant === "floating";
  const isOverlay = frame.variant === "overlay";
  const dragProps = isFloating ? frame.dragHandleProps : {};

  return (
    <Box
      ref={rootRef}
      data-inky-dock=""
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        bgcolor: "var(--q-surface, #FFFFFF)",
        color: "var(--q-text, inherit)",
      }}
    >
      {/* Dock bar: view switch + placement. In floating mode it is also the drag handle. */}
      <Box
        {...dragProps}
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          height: 40,
          px: 1,
          borderBottom: "1px solid var(--q-border-subtle, #ECE9E4)",
          bgcolor: isFloating ? "var(--q-bg-subtle, #FAF9F7)" : "var(--q-surface, #FFFFFF)",
          userSelect: "none",
        }}
      >
        {isFloating && (
          <DragIndicator aria-hidden="true" sx={{ fontSize: 18, color: "var(--q-faint, #716C65)", mr: 0.25 }} />
        )}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(event, next) => next && setView(next)}
          aria-label="Inky view"
          sx={{
            "& .MuiToggleButton-root": {
              border: 0,
              borderRadius: "var(--q-radius-pill, 999px) !important",
              px: 1.25,
              py: 0.25,
              fontSize: 12.5,
              fontWeight: 600,
              textTransform: "none",
              color: "var(--q-muted, #5E5A54)",
              "&.Mui-selected": { bgcolor: "var(--q-accent-selected, #E7E3FA)", color: "var(--q-accent-strong, #2F2096)" },
            },
          }}
        >
          <ToggleButton value="chat">
            Chat
          </ToggleButton>
          <ToggleButton value="history">
            History
          </ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        {!isOverlay && (
          <Tooltip title={`Move Inky (${formatShortcut("Mod+\\")})`}>
            <IconButton
              size="small"
              aria-label="Move Inky"
              aria-haspopup="menu"
              aria-expanded={positionMenuAnchor ? "true" : undefined}
              onClick={(event) => setPositionMenuAnchor(event.currentTarget)}
            >
              {DOCK_POSITIONS.find((p) => p.side === dock.side)?.icon || <ViewSidebarOutlined fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={`Close Inky (${formatShortcut("Mod+J")})`}>
          <IconButton size="small" aria-label="Close Inky" onClick={frame.close}>
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Menu
        anchorEl={positionMenuAnchor}
        open={Boolean(positionMenuAnchor)}
        onClose={() => setPositionMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        <Typography component="li" variant="caption" sx={{ display: "block", px: 2, pt: 0.5, pb: 0.75, color: "var(--q-muted, #5E5A54)", fontWeight: 600 }}>
          Inky position
        </Typography>
        {DOCK_POSITIONS.map((position) => (
          <MenuItem
            key={position.side}
            selected={dock.side === position.side}
            onClick={() => {
              setDock({ side: position.side, open: true });
              setPositionMenuAnchor(null);
            }}
          >
            <ListItemIcon>{position.icon}</ListItemIcon>
            <ListItemText>{position.label}</ListItemText>
            {dock.side === position.side && <Check fontSize="small" sx={{ ml: 1, color: "var(--q-accent, #4F3BD6)" }} />}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            setPositionMenuAnchor(null);
            setShortcutsHelpOpen(true);
          }}
        >
          <ListItemIcon>
            <KeyboardOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Keyboard shortcuts</ListItemText>
        </MenuItem>
      </Menu>

      {/* Both views stay mounted so switching never interrupts Inky. */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", position: "relative" }}>
        <Box sx={{ height: "100%", display: view === "chat" ? "block" : "none" }}>
          {chatPanel}
        </Box>
        <Box sx={{ height: "100%", display: view === "history" ? "block" : "none" }}>
          {historyPanel}
        </Box>
      </Box>
    </Box>
  );
}

export default ChatAgentDock;
